import React from 'react';
import Order from "../../models/Order";
import {LOCALSTORAGEKEYS} from "../../config/constants";
import AlmacenesService from "../../services/almacenes.service";
import Utils from "../../utils/Utils";
import UsuariosService from "../../services/usuarios.service";
import ReponedoresService from "../../features/reponedores/api/reponedores.api";
import AuthService from "../../services/auth.service";
import Roles, { esCuentaPantalla } from "../../config/roles";

export const StateContext = React.createContext();

export class StateProvider extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sidebarShow: true,
            headerVisible: true,
            sidebarUnfoldable: false,
            messages: [], // Quitar, pero comprobar si nadie lo usa
            loading: false,
            products: [],
            toasts: [],
            order: new Order(),
            filters: [],
            year: parseInt(localStorage.getItem(LOCALSTORAGEKEYS.EJERCICIO), 10) || new Date().getFullYear(),
            employees: [],
            almacenes: [],
            notificacionesActivas: false,
            asignacionesReponedores: [],
            refrescarContadores: {
                traspasos: 0,
                incidencias: 0,
                inventarios: 0,
            }
        };
    }

    /**
     * Carga empleados, almacenes y asignaciones de reponedores del usuario con sesión.
     * Se llama al montar (sesión ya abierta) y desde Login al iniciar sesión.
     * DECISION: sin sesión no se pide nada. Antes se pedía igual al abrir la app en el
     * login, fallaba sin token y la lista de almacenes quedaba vacía hasta recargar:
     * el stock de la etiqueta salía con el almacén "Desconocido".
     * Tampoco se piden para las cuentas de pantalla de colas: no los usan y el backend
     * les responde 403 fuera de /colas y /entradas-cola.
     */
    cargarDatosIniciales = async () => {
        const usuario = AuthService.getCurrentUser();
        if (!usuario || esCuentaPantalla(usuario)) return;
        try {
            const [employees, almacenes, asignacionesReponedores] = await Promise.all([
                UsuariosService.listarUsuariosMap(),
                AlmacenesService.obtenerAlmacenes(true),
                ReponedoresService.list().catch(() => [])
            ]);

            this.updateState({ employees, almacenes, asignacionesReponedores });
        } catch (error) {
            console.error('Error cargando datos iniciales:', error);
        }
    };

    async componentDidMount() {
        await this.cargarDatosIniciales();

        if (!('serviceWorker' in navigator)) return;

        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return;

        const sub = await registration.pushManager.getSubscription();

        if (sub) {
            this.setState({ notificacionesActivas: true });
        }

        // Escuchar mensajes del service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data.type === 'reponedor_asignacion') {
                this.recargarAsignacionesReponedores();
            }
        });
    }

    updateState = (newState) => {
        this.setState(newState);
    };

    getNombreAlmacen = (codigo) => {
        const cod = String(codigo ?? '').trim();
        if (!cod) return 'Desconocido';
        const item = this.state.almacenes.find(a => String(a.CODIGO).trim() === cod);
        return Utils.toCapitalize(item?.NOMBRE) ?? 'Desconocido';
    };

    /**
     * Recarga las asignaciones de reponedores desde el backend.
     * Se llama cuando se recibe notificación push o después de operaciones CRUD.
     */
    recargarAsignacionesReponedores = async () => {
        try {
            const asignacionesReponedores = await ReponedoresService.list();
            this.updateState({ asignacionesReponedores });
        } catch (error) {
            console.error('Error recargando asignaciones de reponedores:', error);
        }
    };

    /**
     * Fuerza la recarga inmediata del contador de traspasos en el header.
     * Llamar después de crear, actualizar o eliminar un traspaso.
     */
    refrescarContadorTraspasos = () => {
        this.setState(prevState => ({
            refrescarContadores: {
                ...prevState.refrescarContadores,
                traspasos: Date.now()
            }
        }));
    };

    /**
     * Fuerza la recarga inmediata del contador de incidencias en el header.
     * Llamar después de crear, actualizar o eliminar una incidencia.
     */
    refrescarContadorIncidencias = () => {
        this.setState(prevState => ({
            refrescarContadores: {
                ...prevState.refrescarContadores,
                incidencias: Date.now()
            }
        }));
    };

    /**
     * Fuerza la recarga inmediata del contador de inventarios sin revisar en el header.
     * Llamar después de crear, eliminar o revisar un inventario.
     */
    refrescarContadorInventarios = () => {
        this.setState(prevState => ({
            refrescarContadores: {
                ...prevState.refrescarContadores,
                inventarios: Date.now()
            }
        }));
    };

    /**
     * Verifica si el usuario actual puede acceder al Panel de Peticiones.
     * ADMIN siempre tiene acceso.
     * REPONEDOR solo si está en alguna asignación.
     * @returns {boolean}
     */
    puedeAccederPanelPeticiones = () => {
        const usuarioActual = AuthService.getCurrentUser();
        const roles = usuarioActual?.roles || [];
        const usuarioId = usuarioActual?.id;

        // ADMIN siempre tiene acceso
        if (roles.includes(Roles.ADMIN.role)) {
            return true;
        }

        // Si no es ADMIN, verificar si es reponedor asignado
        if (!roles.includes(Roles.REPONEDOR.role)) {
            return false;
        }

        const asignaciones = this.state.asignacionesReponedores || [];

        return asignaciones.some((asignacion) => {
            return Array.isArray(asignacion.usuarios) &&
                asignacion.usuarios.some((id) => Number(id) === Number(usuarioId));
        });
    };

    render() {
        return (
            <StateContext.Provider
                value={{
                    state: this.state,
                    updateState: this.updateState,
                    getNombreAlmacen: this.getNombreAlmacen,
                    puedeAccederPanelPeticiones: this.puedeAccederPanelPeticiones,
                    cargarDatosIniciales: this.cargarDatosIniciales,
                    recargarAsignacionesReponedores: this.recargarAsignacionesReponedores,
                    refrescarContadorTraspasos: this.refrescarContadorTraspasos,
                    refrescarContadorIncidencias: this.refrescarContadorIncidencias,
                    refrescarContadorInventarios: this.refrescarContadorInventarios
                }}
            >
                {this.props.children}
            </StateContext.Provider>
        );
    }
}
